import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders, faStar } from '@fortawesome/free-solid-svg-icons';
import './Flashcards.css';

/**
 * Deck browse filters (level + rating).
 *
 * Both filters map straight onto server query params:
 *   level  -> `difficulty` (1 | 2 | 3), omitted for "Any"
 *   rating -> `minRating` (>= on the 1–10 scale) and/or `maxRating`
 *             (strictly < on the 1–10 scale), each omitted for "Any"
 *
 * `minRating` excludes unrated decks on the server, so "Any rating" MUST send
 * no param at all rather than `minRating=1`. The same rule applies to
 * `maxRating`: only send it when the bucket actually has an upper bound.
 *
 * `maxRating` is EXCLUSIVE (average rating strictly < maxRating), so the
 * "1 to 5" bucket is `minRating=1&maxRating=6` — the exact complement of
 * "6+" (a deck averaging 5.4 belongs in "1 to 5").
 */
export const LEVEL_OPTIONS = [
  { value: null, label: 'Any level', chipLabel: null },
  { value: 1, label: 'Beginner', chipLabel: 'Beginner', dotClass: 'fc-dot-beginner' },
  { value: 2, label: 'Intermediate', chipLabel: 'Intermediate', dotClass: 'fc-dot-intermediate' },
  { value: 3, label: 'Advanced', chipLabel: 'Advanced', dotClass: 'fc-dot-advanced' },
];

export const RATING_OPTIONS = [
  { minRating: null, maxRating: null, label: 'Any rating', chipLabel: null },
  { minRating: 1, maxRating: 6, label: '1 to 5', chipLabel: 'Rated 1–5' },
  { minRating: 6, maxRating: null, label: '6+', chipLabel: 'Rated 6+' },
  { minRating: 7, maxRating: null, label: '7+', chipLabel: 'Rated 7+' },
  { minRating: 8, maxRating: null, label: '8+', chipLabel: 'Rated 8+' },
  { minRating: 9, maxRating: null, label: '9+', chipLabel: 'Rated 9+' },
];

export const findLevelOption = (value) =>
  LEVEL_OPTIONS.find((o) => o.value === value) || LEVEL_OPTIONS[0];

export const findRatingOption = (minRating, maxRating) =>
  RATING_OPTIONS.find((o) => o.minRating === minRating && o.maxRating === maxRating) ||
  RATING_OPTIONS[0];

const DeckFilters = ({
  difficulty,
  minRating,
  maxRating,
  onChangeDifficulty,
  onChangeRating,
  onClear,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const buttonRef = useRef(null);

  // A rating bucket (even one with both bounds, e.g. "1 to 5") is a single
  // active filter — count on `minRating` alone, `maxRating` never applies
  // without it.
  const activeCount = (difficulty ? 1 : 0) + (minRating ? 1 : 0);

  // Close on outside click / Escape. Escape returns focus to the trigger.
  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        if (buttonRef.current) buttonRef.current.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  // `isSelected` / `onSelect` take the whole option rather than a single
  // value so rating buckets (which carry both `minRating` and `maxRating`)
  // and level options (single `value`) can share this renderer.
  const renderOptions = (options, isSelected, onSelect, withStar) => (
    <div className="fc-filter-options">
      {options.map((option) => (
        <button
          key={option.label}
          type="button"
          className={`fc-filter-option ${
            isSelected(option) ? 'fc-filter-option-on' : ''
          }`}
          aria-pressed={isSelected(option)}
          onClick={() => onSelect(option)}
        >
          {option.dotClass && <span className={`fc-filter-dot ${option.dotClass}`} />}
          {withStar && option.minRating !== null && (
            <FontAwesomeIcon icon={faStar} className="fc-filter-option-star" />
          )}
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="fc-filter-anchor" ref={anchorRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`fc-filter-btn ${activeCount > 0 || open ? 'fc-filter-btn-on' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <FontAwesomeIcon icon={faSliders} />
        <span className="fc-filter-btn-label">Filters</span>
        {activeCount > 0 && (
          <span className="fc-filter-count" aria-label={`${activeCount} active`}>
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fc-filter-panel" role="dialog" aria-label="Filter decks">
          <div className="fc-filter-group">
            <div className="fc-filter-group-label">Level</div>
            {renderOptions(
              LEVEL_OPTIONS,
              (option) => option.value === difficulty,
              (option) => onChangeDifficulty(option.value),
              false
            )}
          </div>

          <div className="fc-filter-group">
            <div className="fc-filter-group-label">Rating</div>
            {renderOptions(
              RATING_OPTIONS,
              (option) => option.minRating === minRating && option.maxRating === maxRating,
              (option) => onChangeRating(option.minRating, option.maxRating),
              true
            )}
            <p className="fc-filter-hint">
              Filtering by rating hides decks with no reviews yet.
            </p>
          </div>

          <div className="fc-filter-panel-footer">
            <button
              type="button"
              className="fc-filter-clear"
              onClick={onClear}
              disabled={activeCount === 0}
            >
              Clear filters
            </button>
            <button
              type="button"
              className="fc-btn fc-btn-primary fc-btn-sm"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeckFilters;
